import React, { useCallback, useState } from "react";

import { gql } from "@apollo/client";
import {
  useFeatureFlags,
  useNorthbeamMutation,
  useNorthbeamQuery,
} from "@hooks/http";
import { type FetchMMMAlertEmailList } from "@north-beam/nb-client-side";
import {
  type AddMMMAlertEmailRecipient,
  type AddMMMAlertEmailRecipientVariables,
  type RemoveMMMAlertEmailRecipient,
  type RemoveMMMAlertEmailRecipientVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";

import { SimpleNotificationsContainer } from "./simple-notifications-container";

export const FETCH_MMM_NOTIFICATIONS = gql`
  query FetchMMMAlertEmailList {
    me {
      mmm {
        enabled
        alertEmailRecipients
      }
    }
  }
`;

const ADD_MMM_NOTIFICATION_EMAIL = gql`
  mutation AddMMMAlertEmailRecipient($email: String!) {
    addMMMAlertEmailRecipient(email: $email)
  }
`;

const REMOVE_MMM_NOTIFICATION_EMAIL = gql`
  mutation RemoveMMMAlertEmailRecipient($email: String!) {
    removeMMMAlertEmailRecipient(email: $email)
  }
`;

// Note: This component's purpose is to encapsulate all of the MMM-specific
// notification logic and to pass back some information to the parent (../notifications.tsx)
// This is because (1) notificaitons.tsx is already spaghetti and I don't want to add to it
// (2) sending logic up the tree so that the page can still work as a single unit rather than MMM
// alerts editing acting differently
export function MmmEmailNotifications({
  setSynced,
  setLoading,
}: {
  setSynced: (isSynced: bool) => void;
  setLoading: (isLoading: bool) => void;
}) {
  const [mmmNotificationEmails, setMMMNotificationEmails] = useState<string[]>(
    [],
  );

  const { hasFeatureFlag } = useFeatureFlags();
  const {
    data,
    loading,
    refetch: _refetch,
  } = useNorthbeamQuery<FetchMMMAlertEmailList>(FETCH_MMM_NOTIFICATIONS, {
    onCompleted: (initialLoadData) => {
      setMMMNotificationEmails(initialLoadData.me.mmm.alertEmailRecipients);
      setLoading(false);
    },
  });

  // Hack for existing open bug (since 2020???)
  // https://github.com/apollographql/react-apollo/issues/3862
  const refetch = useCallback(() => {
    setTimeout(() => _refetch(), 0);
  }, [_refetch]);

  const [addAlertRecipient] = useNorthbeamMutation<
    AddMMMAlertEmailRecipient,
    AddMMMAlertEmailRecipientVariables
  >(ADD_MMM_NOTIFICATION_EMAIL, {
    refetchQueries: [{ query: FETCH_MMM_NOTIFICATIONS }],
    awaitRefetchQueries: true,
    async onCompleted() {
      refetch();
      setMMMNotificationEmails(data.me.mmm.alertEmailRecipients);
      setSynced(true);
    },
  });
  const [removeAlertRecipient] = useNorthbeamMutation<
    RemoveMMMAlertEmailRecipient,
    RemoveMMMAlertEmailRecipientVariables
  >(REMOVE_MMM_NOTIFICATION_EMAIL, {
    refetchQueries: [{ query: FETCH_MMM_NOTIFICATIONS }],
    awaitRefetchQueries: true,
    onCompleted: async () => {
      refetch();
      setMMMNotificationEmails(data.me.mmm.alertEmailRecipients);
      setSynced(true);
    },
  });

  if (
    !hasFeatureFlag("enableMmmAlertEmails") ||
    loading ||
    !data?.me.mmm.enabled
  ) {
    return null;
  }

  return (
    <SimpleNotificationsContainer
      notificationName="MMM alert emails"
      notificationEmails={mmmNotificationEmails}
      onAddEmail={async (email) => {
        setSynced(false);
        setMMMNotificationEmails((prev) =>
          Array.from(new Set([...prev, email])),
        );
        addAlertRecipient({ variables: { email } });
      }}
      onRemoveEmail={async (email) => {
        setSynced(false);
        setMMMNotificationEmails((prev) =>
          prev.filter((prevEmail) => prevEmail !== email),
        );
        removeAlertRecipient({ variables: { email } });
      }}
    />
  );
}
